import * as vp from 'vopay';
import { FunctionComponent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const { Table } = vp;

type KeyValuePair = {
  display: string;
  value: string;
};

type TablePropsHandlers = {
  agreement: () => vp.TableProps;
};

type AgreementTableProps = {
  agreementDetails: vp.AgreementDetailsResponse;
  accountToken: string;
  signingAuthorityInfo: vp.SigningAuthorityInfoResponse;
  onClick?: () => void;
};

type FC = FunctionComponent<AgreementTableProps>;

const AgreementTable: FC = ({ agreementDetails, signingAuthorityInfo }: AgreementTableProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'AgreementTable' });

  const getTableProps = useCallback(
    (handler: keyof TablePropsHandlers): vp.TableProps => {
      const handlers: TablePropsHandlers = {
        agreement: () => {
          const agreementInfoPairs: KeyValuePair[] = [
            {
              display: t('agreementStatus', { defaultValue: 'Agreement Status' }),
              value: agreementDetails?.AgreementStatus?.replace('Agreement ', '') || '',
            },
            {
              display: t('requestedOn', { defaultValue: 'Requested On' }),
              value:
                agreementDetails?.AgreementStatus === 'Agreement Sent' ||
                agreementDetails?.AgreementStatus === 'Agreement Viewed' ||
                agreementDetails?.AgreementStatus === 'Agreement Completed'
                  ? agreementDetails?.DateAdded?.split(' ')[0] || '-'
                  : '-',
            },
            {
              display: t('numberOfSigners', { defaultValue: 'Number of Signers' }),
              value: !signingAuthorityInfo.Success
                ? '0'
                : String(Object.values(signingAuthorityInfo?.SigningAuthority)?.length || '') || '',
            },
          ];

          const agreementInfoProps: vp.TableProps = {
            name: 'account-agreement-details',
            variant: 'pair',
            columns: {
              detailKey: { header: t('tableHeader', { defaultValue: 'Agreement Details' }) },
              detailValue: { header: '' },
            },
            rows: agreementInfoPairs.map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
          };

          return agreementInfoProps;
        },
      };
      return handlers[handler]();
    },
    [signingAuthorityInfo, t],
  );

  return <Table {...getTableProps('agreement')} />;
};

export default AgreementTable;
